<script lang="ts" setup>
import { computed } from 'vue';
import AlertTargetList from '@/alert/components/alertTargetList/AlertTargetList.vue';
import { useInternational } from '@/common/locale';
import { isNil } from 'lodash-es';
import { AlertRuleV9RequestSubType } from '@/api-client/metav8Over/model/alert-rule-v9-request-sub-type';
import { CHECK_BY_TARGET } from '@/alert/utils/define';
import { isCustomAlertRule } from '@/alert/utils/alertMetricSubType.utils';
import type { ProcessedMetricInfo } from './eventAlertInformation.types';
import ThresholdDisplay from './ThresholdDisplay.vue';

interface Props {
  metric: ProcessedMetricInfo;
  labelWidth: string;
  ruleCriteria?: string;
  metricSubType?: string;
}

const props = defineProps<Props>();

const { t } = useInternational();

const isCustomRule = computed(() =>
  isCustomAlertRule({
    subType: props.metricSubType,
    metricConditions: [{ conditionType: props.metric.conditionType }],
  }),
);

const isFormulaCondition = computed(() => props.metric.conditionType === 'formula');

const useSingleLevelThreshold = computed(
  () =>
    props.metricSubType === AlertRuleV9RequestSubType.CUSTOM ||
    props.metricSubType === AlertRuleV9RequestSubType.COMPOUND,
);

const hasThreshold = computed(
  () =>
    !!props.metric.comparator ||
    !isNil(props.metric.warningValue) ||
    !isNil(props.metric.criticalValue),
);

const alertStatNameLabel = computed(() =>
  isCustomRule.value && isFormulaCondition.value ? t('WORD.FORMULA') : t('WORD.ALERT_STAT_NAME'),
);

// CHECK_BY_TARGET(단일): Tag/Target이 occurrence items에 이미 표시됨 → 지표 박스 내 중복 제거
const showTagsTargets = computed(
  () => !isCustomRule.value && props.ruleCriteria !== CHECK_BY_TARGET,
);

const thresholdAlertLevel = computed(() =>
  useSingleLevelThreshold.value ? props.metric.alertLevel : undefined,
);
</script>

<template>
  <v-stack
    :spacing="12"
    class="event-alert-occurrence-message__content"
  >
    <h-stack
      v-if="props.metric.alertStatName"
      class="event-alert-occurrence-message__content-item"
      :spacing="12"
    >
      <span>{{ alertStatNameLabel }}</span>
      <span>{{ props.metric.alertStatName }}</span>
    </h-stack>

    <h-stack
      v-if="props.metric.aggregation"
      class="event-alert-occurrence-message__content-item"
      :spacing="12"
    >
      <span>{{ `${t('WORD.TARGET')} ${t('WORD.AGGREGATION')}` }}</span>
      <span>{{ props.metric.aggregation }}</span>
    </h-stack>

    <h-stack
      v-if="props.metric.formulaAlias"
      class="event-alert-occurrence-message__content-item"
      :spacing="12"
    >
      <span>{{ t('WORD.ALIAS') }}</span>
      <span>{{ props.metric.formulaAlias }}</span>
    </h-stack>

    <h-stack
      v-if="hasThreshold"
      class="event-alert-occurrence-message__content-item event-alert-occurrence-message__content-item--threshold"
      :spacing="12"
    >
      <span>{{ t('WORD.THRESHOLD') }}</span>
      <threshold-display
        :evaluate-value="props.metric.evaluateValue"
        :during-time-interval="props.metric.duringTimeInterval"
        :comparator="props.metric.comparator"
        :warning-value="props.metric.warningValue"
        :critical-value="props.metric.criticalValue"
        :unit="props.metric.unit"
        :alert-level="thresholdAlertLevel"
        :show-label="false"
      />
    </h-stack>

    <h-stack
      v-if="!isCustomRule && props.metric.alertValue"
      class="event-alert-occurrence-message__content-item"
      :spacing="12"
    >
      <span>{{ t('WORD.ALERT_VALUE') }}</span>
      <span
        >{{ props.metric.alertValue }}{{ props.metric.unit ? ` ${props.metric.unit}` : '' }}</span
      >
    </h-stack>

    <template v-if="showTagsTargets">
      <h-stack
        v-if="props.metric.tags && props.metric.tags.length"
        class="event-alert-occurrence-message__content-item list-occurrence-item"
        :spacing="12"
      >
        <span>{{ t('WORD.TAG') }}</span>
        <alert-target-list
          :target-list="props.metric.tags"
          :is-expand="true"
        />
      </h-stack>

      <h-stack
        v-if="props.metric.targets && props.metric.targets.length"
        class="event-alert-occurrence-message__content-item list-occurrence-item"
        :spacing="12"
      >
        <span>{{ t('WORD.TARGET') }}</span>
        <alert-target-list
          :target-list="props.metric.targets"
          :is-expand="true"
          :is-clickable="true"
        />
      </h-stack>
    </template>
  </v-stack>
</template>

<style scoped lang="scss">
.event-alert-occurrence-message__content-item {
  height: 20px;

  > span:first-of-type {
    min-width: v-bind('labelWidth');
    font-weight: 500;
  }

  &.list-occurrence-item {
    align-items: flex-start;
    height: auto;
    min-height: 20px;

    > span:first-of-type {
      margin-top: 6px;
    }
  }

  :deep(.target-list) {
    flex: 1;
    min-width: 0;
  }

  &--threshold {
    align-items: flex-start;
    height: auto;
    min-height: 38px;

    :deep(.threshold-display) {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
